import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import CTAButton from '../../components/base/CTAButton';
import { useSEO, generateWebPageSchema } from '../../utils/seo';

export default function CinewallInspiratie() {
  useSEO({
    title: 'Cinewall inspiratie | Luxe maatwerk cinewalls',
    description: 'Bekijk cinewall inspiratie in moderne en luxe interieurs.',
    keywords: 'cinewall inspiratie, cinewall voorbeelden, luxe cinewall, moderne cinewall',
    canonical: `${import.meta.env.VITE_SITE_URL}/cinewall-inspiratie`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'ImageGallery',
      name: 'Cinewall inspiratie',
      description: 'Inspiratie voor luxe maatwerk cinewalls met moderne en klassieke designs',
      url: `${import.meta.env.VITE_SITE_URL}/cinewall-inspiratie`,
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const inspirationCategories = [
    {
      title: 'Moderne cinewalls',
      description: 'Strakke lijnen, minimalistische uitstraling en tijdloos design',
      image: 'https://public.readdy.ai/ai/img_res/03f8abb78994f56d4b5b7554f44539e2.jpg',
      features: ['Strakke panelen', 'Verborgen opbergruimte', 'Subtiele LED-verlichting']
    },
    {
      title: 'Luxe cinewalls',
      description: 'Premium materialen, warme uitstraling en exclusieve afwerking',
      image: 'https://readdy.ai/api/search-image?query=high-end%20luxury%20cinewall%20with%20premium%20natural%20stone%20panels%20integrated%20fireplace%20large%20television%20warm%20ambient%20lighting%20elegant%20living%20room%20beige%20cream%20tones%20gold%20metallic%20accents%20sophisticated%20interior%20design%20expensive%20materials%20textured%20wall%20panels%20indirect%20lighting%20luxury%20furniture%20modern%20classic%20style&width=800&height=600&seq=insp002&orientation=landscape',
      features: ['Natuursteen', 'Warme houtsoorten', 'Gouden accenten']
    },
    {
      title: 'LED-verlichting en nissen',
      description: 'Sfeervolle verlichting die uw cinewall tot leven brengt',
      image: 'https://readdy.ai/api/search-image?query=luxury%20cinewall%20with%20dramatic%20LED%20strip%20lighting%20illuminated%20wall%20niches%20integrated%20display%20shelves%20ambient%20backlighting%20modern%20living%20room%20dark%20panels%20warm%20white%20LED%20lights%20architectural%20lighting%20design%20floating%20shelves%20indirect%20lighting%20sophisticated%20atmosphere%20premium%20interior&width=800&height=600&seq=insp003&orientation=landscape',
      features: ['Indirecte verlichting', 'Verlichte nissen', 'Dimbare LED-strips']
    },
    {
      title: 'Opbergruimte en indeling',
      description: 'Functioneel en esthetisch: alles op zijn plaats',
      image: 'https://readdy.ai/api/search-image?query=luxury%20cinewall%20with%20integrated%20storage%20cabinets%20hidden%20compartments%20organized%20shelving%20system%20modern%20living%20room%20clean%20design%20built-in%20drawers%20closed%20cabinets%20open%20display%20niches%20minimalist%20handles%20premium%20wood%20finish%20sophisticated%20organization%20seamless%20integration&width=800&height=600&seq=insp004&orientation=landscape',
      features: ['Verborgen kasten', 'Open nissen', 'Geïntegreerde laden']
    },
    {
      title: 'Afmetingen en verhoudingen',
      description: 'Perfecte balans tussen tv, haard en omgeving',
      image: 'https://readdy.ai/api/search-image?query=perfectly%20proportioned%20luxury%20cinewall%20with%20balanced%20composition%20large%20television%20centered%20fireplace%20below%20symmetrical%20design%20modern%20living%20room%20ideal%20viewing%20height%20proper%20spacing%20elegant%20proportions%20architectural%20harmony%20premium%20materials%20sophisticated%20layout%20professional%20design&width=800&height=600&seq=insp005&orientation=landscape',
      features: ['Ideale kijkhoogte', 'Symmetrische indeling', 'Harmonieuze verhoudingen']
    },
    {
      title: 'Cinewall met waterdamp haard',
      description: 'Ultiem luxe met realistische vlammen en waterdamp',
      image: 'https://readdy.ai/api/search-image?query=ultra%20luxury%20cinewall%20with%20realistic%20water%20vapor%20fireplace%20integrated%20television%20modern%20living%20room%20authentic%20flame%20effect%20mist%20technology%20premium%20black%20panels%20warm%20beige%20walls%20sophisticated%20lighting%20high-end%20interior%20design%20elegant%20atmosphere%20contemporary%20style%20expensive%20materials&width=800&height=600&seq=insp006&orientation=landscape',
      features: ['Realistische vlammen', 'Geen warmte', 'Veilig en luxe']
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-serif text-gray-900 mb-6">
            Cinewall inspiratie
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
            Ontdek de mogelijkheden van een cinewall op maat. Van moderne minimalistische designs 
            tot luxe uitvoeringen met natuursteen en premium materialen.
          </p>
        </div>
      </section>

      {/* Inspiration Grid */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto space-y-24">
          {inspirationCategories.map((category, index) => (
            <div 
              key={index} 
              className={`grid md:grid-cols-2 gap-12 items-center ${
                index % 2 === 1 ? 'md:flex-row-reverse' : ''
              }`}
            >
              <div className={index % 2 === 1 ? 'md:order-2' : ''}>
                <div className="aspect-[4/3] rounded-lg overflow-hidden shadow-xl">
                  <img 
                    src={category.image}
                    alt={category.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
              
              <div className={index % 2 === 1 ? 'md:order-1' : ''}>
                <h2 className="text-4xl font-serif text-gray-900 mb-4">
                  {category.title}
                </h2>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  {category.description}
                </p>
                <ul className="space-y-3">
                  {category.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center">
                      <i className="ri-check-line text-stone-800 text-xl mr-3"></i>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Design Tips */}
      <section className="py-20 px-6 bg-stone-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-serif text-gray-900 mb-12 text-center">
            Tips voor uw cinewall ontwerp
          </h2>
          
          <div className="space-y-6">
            <div className="bg-white p-8 rounded-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Kies voor tijdloze materialen
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Natuursteen, hoogwaardig hout en matte afwerkingen blijven jarenlang mooi. 
                Vermijd trendy kleuren en kies voor neutrale tinten die passen bij elk interieur.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Denk aan de juiste verhoudingen
              </h3>
              <p className="text-gray-700 leading-relaxed">
                De tv moet op ooghoogte hangen wanneer u zit. De haard komt idealiter onder de tv, 
                met voldoende afstand voor ventilatie. Wij adviseren u over de perfecte indeling.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Verlichting maakt het verschil
              </h3>
              <p className="text-gray-700 leading-relaxed">
                LED-verlichting in nissen en achter panelen zorgt voor sfeer en diepte. 
                Kies voor dimbare verlichting zodat u de sfeer kunt aanpassen aan het moment.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Functionaliteit en esthetiek
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Een cinewall is niet alleen mooi, maar ook praktisch. Denk aan voldoende opbergruimte 
                voor apparatuur, kabels en persoonlijke spullen. Alles netjes weggewerkt.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-stone-800 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-serif mb-6">
            Klaar om uw eigen cinewall te realiseren?
          </h2>
          <p className="text-xl text-stone-200 mb-10 leading-relaxed">
            Vraag een vrijblijvende offerte aan of plan een showroombezoek om materialen en haarden in het echt te ervaren
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/cinewall-offerte">
              <CTAButton variant="light">
                Vraag gratis offerte aan
              </CTAButton>
            </Link>
            <Link to="/cinewall-showroom">
              <CTAButton variant="outline-light">
                Plan showroomafspraak
              </CTAButton>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
